"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import { ChevronRight, BookOpen, Rocket, Code, Shield, FileText, Lightbulb, type LucideIcon } from "lucide-react"
import Header from "@/components/header"
import Footer from "@/components/footer"

const navigation: { title: string; icon: LucideIcon; items: { title: string; href: string }[] }[] = [
    {
        title: "Getting Started",
        icon: Rocket,
        items: [
            { title: "Quick Start Guide", href: "/docs/getting-started/quick-start" },
            { title: "Platform Overview", href: "/docs/getting-started/platform-overview" },
            { title: "Understanding Presales", href: "/docs/getting-started/understanding-presales" },
            { title: "Best Practices", href: "/docs/getting-started/best-practices" },
        ],
    },
    {
        title: "Smart Contracts",
        icon: Code,
        items: [
            { title: "Contract Overview", href: "/docs/smart-contracts/contract-overview" },
            { title: "Deployed Contracts", href: "/docs/smart-contracts/deployed-contracts" },
            { title: "Security Features", href: "/docs/smart-contracts/security-features" },
            { title: "Token Standards", href: "/docs/smart-contracts/token-standards" },
            { title: "Liquidity Locking", href: "/docs/smart-contracts/liquidity-locking" },
        ],
    },
    {
        title: "Security",
        icon: Shield,
        items: [
            { title: "Security Measures", href: "/docs/security/security-measures" },
            { title: "Audit Reports", href: "/docs/security/audit-reports" },
            { title: "KYC Process", href: "/docs/security/kyc-process" },
            { title: "Anti-Rug Features", href: "/docs/security/anti-rug-features" },
        ],
    },
    {
        title: "API Reference",
        icon: FileText,
        items: [
            { title: "REST API", href: "/docs/api/rest-api" },
            { title: "Web3 Integration", href: "/docs/api/web3-integration" },
            { title: "Webhooks", href: "/docs/api/webhooks" },
            { title: "Rate Limits", href: "/docs/api/rate-limits" },
        ],
    },
    {
        title: "Guides",
        icon: Lightbulb,
        items: [
            { title: "How to Create a Presale", href: "/docs/guides/create-presale" },
            { title: "Understanding Tokenomics", href: "/docs/guides/understanding-tokenomics" },
            { title: "Marketing Your Presale", href: "/docs/guides/marketing-presale" },
            { title: "Managing Raised Funds", href: "/docs/guides/managing-funds" },
        ],
    },
]

export function DocsSidebar() {
    const currentPath = usePathname()
    return (
        <aside className="w-64 shrink-0 hidden lg:block">
            <nav className="sticky top-24 space-y-6">
                {navigation.map((section) => {
                    const Icon = section.icon
                    return (
                        <div key={section.title}>
                            <h4 className="flex items-center gap-2 font-semibold text-sm mb-2">
                                <Icon className="h-4 w-4 text-primary" />
                                {section.title}
                            </h4>
                            <ul className="space-y-1 border-l border-border/50 pl-4">
                                {section.items.map((item) => (
                                    <li key={item.href}>
                                        <Link
                                            href={item.href}
                                            className={`block text-sm py-1.5 transition-colors ${currentPath === item.href
                                                ? "text-primary font-medium"
                                                : "text-muted-foreground hover:text-foreground"
                                                }`}
                                        >
                                            {item.title}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )
                })}
            </nav>
        </aside>
    )
}

export function DocsBreadcrumb() {
    const pathname = usePathname()
    const segments = pathname.split("/").filter(Boolean)

    const formatSegment = (segment: string) => {
        return segment
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
    }

    return (
        <nav className="flex items-center gap-1 text-sm text-muted-foreground mb-6">
            <Link href="/docs" className="hover:text-foreground transition-colors">
                <BookOpen className="h-4 w-4" />
            </Link>
            {segments.slice(1).map((segment, index) => (
                <span key={segment} className="flex items-center gap-1">
                    <ChevronRight className="h-3 w-3" />
                    {index === segments.length - 2 ? (
                        <span className="text-foreground">{formatSegment(segment)}</span>
                    ) : (
                        <Link
                            href={`/${segments.slice(0, index + 2).join("/")}`}
                            className="hover:text-foreground transition-colors"
                        >
                            {formatSegment(segment)}
                        </Link>
                    )}
                </span>
            ))}
        </nav>
    )
}

export function DocsLayoutClient({ children }: { children: React.ReactNode }) {
    const pathname = usePathname()

    // The /docs index page has its own Header/Footer/layout
    if (pathname === "/docs") {
        return <>{children}</>
    }

    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-1 pt-20">
                <div className="container py-8 md:py-12">
                    <div className="flex gap-10">
                        <DocsSidebar />
                        <div className="flex-1 min-w-0">
                            <DocsBreadcrumb />
                            <article className="prose prose-neutral dark:prose-invert max-w-none">
                                {children}
                            </article>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    )
}
